import { useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  ActionIcon,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import BackButton from "../../../../components/View/BackButton";
import { createWebhook } from "../../../../api/ConfigsApi";

let headerId = 1;

const CreateWebhook = ({ handleDone, ...props }) => {
  const lang = useLanguage((s) => s.language);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState([{ key: "", value: "", id: 0 }]);

  const eventOptions = useMemo(() => {
    return [
      {
        value: "email_open",
        label: lang.workflows.nodes.events.email_open,
      },
      {
        value: "link_click",
        label: lang.workflows.nodes.events.link_click,
      },
      {
        value: "email_answered",
        label: lang.workflows.nodes.events.email_answered,
      },
      {
        value: "linkedin_visited",
        label: lang.workflows.nodes.events.linkedin_visited,
      },
      {
        value: "linkedin_invitation_sent",
        label: lang.workflows.nodes.events.linkedin_invitation_sent,
      },
      {
        value: "linkedin_invitation_accepted",
        label: lang.workflows.nodes.events.linkedin_invitation_accepted,
      },
      {
        value: "linkedin_message_answered",
        label: lang.workflows.nodes.events.linkedin_message_answered,
      },
      {
        value: "linkedin_message_sent",
        label: lang.workflows.nodes.events.linkedin_message_sent,
      },
      {
        value: "sms_got",
        label: lang.workflows.nodes.events.sms_received,
      },
      {
        value: "sms_replied",
        label: lang.workflows.nodes.events.sms_replied,
      },
      {
        value: "all",
        label: lang.workflows.nodes.events.all,
      },
    ];
  }, [lang]);

  const handleOpen = () => {
    setOpened(true);
  };
  const handleClose = () => {
    setOpened(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    let formData = new FormData(e.target);
    let data = Object.fromEntries(formData);

    let headersAsObject = {};
    headers.forEach((item) => {
      if (item.value && item.key) {
        headersAsObject[item.key] = item.value;
      }
    });

    setLoading(true);
    createWebhook({
      name: data?.name,
      url: data.url,
      enabled: true,
      event_type: data?.event,
      headers: headersAsObject,
    })
      .then((response) => {
        notifications.show({
          message: lang.webhooks.created,
          color: "tertiary.8",
        });
        handleClose();
        handleDone();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Headers
  const addHeader = () => {
    setHeaders((prev) => [...prev, { key: "", value: "", id: headerId++ }]);
  };

  const removeHeader = (id) => {
    setHeaders((prev) => {
      let newHeaders = [...prev];

      return newHeaders.filter((x) => x.id !== id);
    });
  };

  const editHeaderItem = (index, field, newValue) => {
    setHeaders((prev) => {
      let newHeaders = [...prev];
      newHeaders[index][field] = newValue;

      return newHeaders;
    });
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        leftSection={<IconPlus size={18} />}
        size="md"
        {...props}
      >
        {lang.webhooks.create.label}
      </Button>

      <Modal
        opened={opened}
        title={lang.webhooks.create.label}
        onClose={handleClose}
        size={"50%"}
      >
        <Stack component={"form"} onSubmit={onSubmit}>
          <Group wrap="nowrap">
            <TextInput
              name="name"
              label={lang.webhooks.create.name}
              placeholder={"My webhook"}
              required
              w={"100%"}
              autoFocus
              data-autofocus
              size="md"
            />

            <Select
              name="event"
              label={lang.webhooks.create.event}
              placeholder={lang.sync_zapier.event_label}
              defaultValue={"email_open"}
              required
              data={eventOptions}
              size="md"
              w={"100%"}
            />
          </Group>

          <TextInput
            name="url"
            label={lang.sync_zapier.hook_url}
            id="new-webhook-url"
            type="url"
            placeholder="https://your-link.com/hook"
            required
            size="md"
          />

          {headers.map((header, index) => (
            <Group key={header.id} align="end" w={"100%"} wrap="nowrap">
              <TextInput
                label={index === 0 ? "Header" : null}
                placeholder="Authorization"
                w={"100%"}
                onChange={(e) => editHeaderItem(index, "key", e.target.value)}
                size="md"
                pattern="^[a-zA-Z0-9'`#$%&*+.^_|~!-]+$"
                required={headers.length > 1}
                title={`${lang.webhooks.create.authorized_chars} ' # $ % & * + . ^ _ | ~ ! -`}
              />
              <TextInput
                label={index === 0 ? lang.webhooks.value : null}
                placeholder="Bearer ..."
                w={"100%"}
                onChange={(e) => editHeaderItem(index, "value", e.target.value)}
                size="md"
                required={headers.length > 1}
              />
              {headers.length > 1 && (
                <Tooltip label={lang.webhooks.create.delete_header}>
                  <ActionIcon
                    size={42}
                    color="red"
                    variant="light"
                    onClick={() => removeHeader(header.id)}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Tooltip label={lang.webhooks.create.add_header}>
                <ActionIcon
                  size={42}
                  color="tertiary.8"
                  variant="light"
                  style={{
                    visibility:
                      index < headers.length - 1 ? "hidden" : "visible",
                  }}
                  onClick={addHeader}
                >
                  <IconPlus size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ))}

          <Group justify="end" mt={"lg"}>
            <BackButton onClick={handleClose} size="md" />
            <Button
              // disabled={
              //   type === "prm" ? !filter || filter?.values?.length === 0 : false
              // }
              leftSection={<IconPlus size={18} />}
              loading={loading}
              type="submit"
              size="md"
            >
              {lang.webhooks.create.submit}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default CreateWebhook;
